// components
import TextField from '@mui/material/TextField';

// hooks
import { useEffect, useRef, useState } from 'react';

// types
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

type WeekHoursFieldProps = {
  value: number;
  onCommit: (hours: number) => void;
  label: string;
  helperText: ReactNode;
  /** contract information cannot be edited; only a zero-hours contract may */
  readOnly?: boolean;
  sx?: SxProps<Theme>;
};

/** committing every keystroke re-renders the whole dialog and restarts the
 *  rollout calculation; a short pause keeps typing instant */
const COMMIT_DELAY_MS = 300;

/** "16.5" → "16,5"; zero shows as "0", so the field is never mysteriously
 *  blank — the planner sees there is nothing to book yet */
const format = (hours: number) => String(hours).replace('.', ',');

/** '' counts as 0 (the planner cleared the field); comma is a decimal point */
const parse = (raw: string) => {
  if (raw.trim() === '') {
    return 0;
  }
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * The week-hours input. A bare controlled number field snaps back to "0" the
 * moment the planner clears it and turns a Dutch decimal comma into NaN — so
 * the field keeps its own text buffer (the same idea as the percentage field).
 * Commits are debounced: only this field re-renders while the planner types,
 * and the dialog (consequences panel, rollout request) follows once they
 * pause. Blur and unmount flush, so a pending value is never lost.
 */
const WeekHoursField = ({
  value,
  onCommit,
  label,
  helperText,
  readOnly = false,
  sx
}: WeekHoursFieldProps) => {
  const [text, setText] = useState(() => format(value));

  const pendingRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // the flush must call the latest handler, not the one it was scheduled with
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const flush = () => {
    clearTimeout(timerRef.current);
    if (pendingRef.current !== null) {
      const hours = pendingRef.current;
      pendingRef.current = null;
      onCommitRef.current(hours);
    }
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  // closing the dialog (or switching tabs) must not swallow a typed value
  useEffect(() => () => flushRef.current(), []);

  // an external change (recorded settings loading, another tab) refreshes the
  // buffer; our own commits — pending or done — leave the typing alone
  useEffect(() => {
    setText((current) =>
      pendingRef.current !== null || parse(current) === value
        ? current
        : format(value)
    );
  }, [value]);

  return (
    <TextField
      size="small"
      sx={sx}
      label={label}
      value={text}
      onChange={(event) => {
        const raw = event.target.value;
        // digits with at most one decimal separator; anything else is not a
        // number of hours and never reaches the buffer
        if (!/^\d*[.,]?\d*$/.test(raw)) {
          return;
        }
        setText(raw);
        const parsed = parse(raw);
        if (parsed === null) {
          return;
        }
        if (parsed === value) {
          // typed back to the committed value: nothing left to send
          pendingRef.current = null;
          clearTimeout(timerRef.current);
          return;
        }
        pendingRef.current = parsed;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, COMMIT_DELAY_MS);
      }}
      // half-typed input ("16,") settles into its committed form
      onBlur={() => {
        flush();
        setText((current) => {
          const parsed = parse(current);
          return format(parsed !== null ? parsed : value);
        });
      }}
      helperText={helperText}
      slotProps={{
        htmlInput: {
          inputMode: 'decimal',
          readOnly
        }
      }}
    />
  );
};

export default WeekHoursField;
